/**
 * A nyomtató beüzemelése egy pultgépen (#178).
 *
 * EGY GOMB, TOKEN NÉLKÜL. A pultosnak nem kell tudnia, mi a token: az
 * „Indító letöltése" a háttérben kiállítja, és a nyomtató címével együtt a
 * letöltött `.bat`/`.sh` fájlba írja. A token csak hash-ként él a szerveren,
 * ezért minden letöltés ÚJ tokent kér — és az ugyanehhez a nyomtatóhoz
 * korábban letöltött indítók leállnak. Ezt a gomb megerősítés előtt kimondja,
 * és azt is, hogyan kell két gépre tartaléknak: egy letöltés, ugyanaz a fájl
 * mindkét gépre.
 *
 * A lépések a választott rendszerhez (Windows / Linux) igazodnak. A kézi
 * parancsok és az USB-s mód a lenyitható „Haladó" részben maradnak.
 */
import { DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Collapse,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Steps,
  Typography,
} from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config/api';

const { Paragraph, Text } = Typography;

/** A pultgép rendszere; az útmutató lépései ehhez igazodnak. */
export type DeskOs = 'windows' | 'linux';

/** Az alapértelmezett választás a böngésző rendszeréből. */
export function detectDeskOs(userAgent: string = navigator.userAgent): DeskOs {
  return /linux|x11/i.test(userAgent) && !/android/i.test(userAgent) ? 'linux' : 'windows';
}

/** A Python Windowsos letöltőoldala — innen a „Windows installer (64-bit)”. */
export const PYTHON_WINDOWS_DOWNLOAD_URL = 'https://www.python.org/downloads/windows/';

/** Ugyanez parancssorból, a Windows saját csomagkezelőjével. */
export const PYTHON_WINGET_COMMAND = 'winget install -e --id Python.Python.3.13';

/** A backend által kiszolgált agentfájlok (`PrintAgentDownloadController`). */
const AGENT_FILES = ['eventbot_print_agent.py', 'requirements.txt'] as const;

export function agentDownloadUrl(file: (typeof AGENT_FILES)[number]): string {
  return `${API_URL}/public/print-agent/download/${file}`;
}

/** Az indítóparancs hálózati (wifis) nyomtatóhoz — ez az alapeset. */
export function agentTcpCommand(token: string, printerHost: string): string {
  return `python eventbot_print_agent.py --server ${API_URL} --token ${token} --printer-host ${printerHost}`;
}

/** Az indítóparancs USB-re kötött nyomtatóhoz. */
export function agentUsbCommand(token: string): string {
  return `python eventbot_print_agent.py --server ${API_URL} --token ${token} --transport usb`;
}

/**
 * Windowsos indító (`.bat`) egy nyomtatóhoz — a pultgépen dupla kattintással
 * indul, parancssor nélkül.
 *
 * - A tokent KÖRNYEZETI VÁLTOZÓBAN adja át (`EVENTBOT_PRINT_TOKEN`), nem
 *   parancssori paraméterként: így nem látszik a folyamatlistában.
 * - Ha az agent még nincs a mappában, letölti a backendről — ugyanarról a
 *   szerverről, amelyikhez kapcsolódni fog, tehát a verziójuk egyezik.
 * - `py` (a Windowsos Python-indító) vagy `python`, amelyik elérhető.
 * - Ha az agent leáll (hálózati hiba után is magától újrapróbál, de egy
 *   összeomlás után nem), 5 másodperc múlva újraindul. Érvénytelen tokennél
 *   (kilépési kód 2) megáll, és kiírja, mit kell tenni — különben végtelenül
 *   próbálkozna egy visszavont tokennel.
 *
 * A szöveg ékezet nélküli: a Windows-konzol kódlapja gépenként eltér.
 */
export function windowsLauncher(agentName: string, token: string, printerHost: string): string {
  const safeName = agentName.replace(/[^\w .()-]/g, '').trim() || 'EventBot';
  return [
    '@echo off',
    'chcp 65001 >nul',
    `title EventBot nyomtato - ${safeName}`,
    'cd /d "%~dp0"',
    `set "SERVER=${API_URL}"`,
    `set "PRINTER=${printerHost}"`,
    `set "EVENTBOT_PRINT_TOKEN=${token}"`,
    'where py >nul 2>nul && (set "PY=py -3") || (set "PY=python")',
    // Minden induláskor frissít: egy javított agent így a már beüzemelt
    // gépekre is eljut. Ideiglenes fájlba tölt, és csak siker után cseréli;
    // ha nincs internet, a meglévővel indul.
    'echo A print agent frissitese...',
    `powershell -NoProfile -Command "try { Invoke-WebRequest -UseBasicParsing -Uri '%SERVER%/public/print-agent/download/eventbot_print_agent.py' -OutFile 'eventbot_print_agent.py.part'; exit 0 } catch { exit 1 }"`,
    'if not errorlevel 1 (',
    '  move /y "eventbot_print_agent.py.part" "eventbot_print_agent.py" >nul',
    ') else (',
    '  if exist "eventbot_print_agent.py.part" del "eventbot_print_agent.py.part"',
    '  if not exist "eventbot_print_agent.py" (',
    '    echo A letoltes nem sikerult - ellenorizze az internetet, es inditsa ujra.',
    '    pause',
    '    exit /b 1',
    '  )',
    '  echo A frissites nem sikerult, a meglevo programmal indul.',
    ')',
    ':start',
    '%PY% eventbot_print_agent.py --server %SERVER% --printer-host %PRINTER%',
    'if errorlevel 2 if not errorlevel 3 (',
    '  echo.',
    '  echo A token ervenytelen vagy visszavontak. Kerjen uj tokent az admin feluleten',
    '  echo ^(Bejelentkeztetes - fogaskerek - Beallitas - Uj token^), es toltse le ujra ezt a fajlt.',
    '  pause',
    '  exit /b 2',
    ')',
    'echo.',
    'echo Az agent leallt. Ujrainditas 5 masodperc mulva - a leallitashoz zarja be ezt az ablakot.',
    'timeout /t 5 >nul',
    'goto start',
    '',
  ].join('\r\n');
}

/**
 * Linuxos indító (`.sh`) — ugyanaz, mint a {@link windowsLauncher}: letölti az
 * agentet, a tokent környezeti változóban adja át, leállás után újraindít, és
 * érvénytelen tokennél (kilépési kód 2) megáll. Indítás: `bash <fájl>.sh`, így
 * futtathatóvá tenni sem kell.
 *
 * Minden érték egyszeres idézőjelben áll: a token base64url, a cím
 * {@link isPrinterHost}-on átment, a név {@link windowsLauncher}-rel azonos
 * szűrésen — egyik sem tartalmazhat idézőjelet.
 */
export function linuxLauncher(agentName: string, token: string, printerHost: string): string {
  const safeName = agentName.replace(/[^\w .()-]/g, '').trim() || 'EventBot';
  return [
    '#!/usr/bin/env bash',
    `# EventBot nyomtato - ${safeName}`,
    'cd "$(dirname "$0")" || exit 1',
    `SERVER='${API_URL}'`,
    `PRINTER='${printerHost}'`,
    `export EVENTBOT_PRINT_TOKEN='${token}'`,
    // Minden induláskor frissít, hogy egy javított agent a már beüzemelt
    // gépekre is eljusson. Ideiglenes fájlba tölt, és csak nem üres eredményt
    // nevez át: egy elbukott `wget` üres fájlt hagyna hátra (mérve, #178). Ha
    // nincs internet, a meglévővel indul.
    'echo "A print agent frissitese..."',
    'URL="$SERVER/public/print-agent/download/eventbot_print_agent.py"',
    'if { curl -fsSL -o eventbot_print_agent.py.part "$URL" || wget -q -O eventbot_print_agent.py.part "$URL"; } \\',
    '  && [ -s eventbot_print_agent.py.part ]; then',
    '  mv eventbot_print_agent.py.part eventbot_print_agent.py',
    'else',
    '  rm -f eventbot_print_agent.py.part',
    '  if [ ! -s eventbot_print_agent.py ]; then',
    '    echo "A letoltes nem sikerult - ellenorizze az internetet, es inditsa ujra."',
    '    exit 1',
    '  fi',
    '  echo "A frissites nem sikerult, a meglevo programmal indul."',
    'fi',
    'while true; do',
    '  python3 eventbot_print_agent.py --server "$SERVER" --printer-host "$PRINTER"',
    '  if [ $? -eq 2 ]; then',
    '    echo "A token ervenytelen vagy visszavontak. Kerjen uj tokent az admin feluleten, es toltse le ujra ezt a fajlt."',
    '    exit 2',
    '  fi',
    '  echo "Az agent leallt. Ujrainditas 5 masodperc mulva - leallitas: Ctrl+C."',
    '  sleep 5',
    'done',
    '',
  ].join('\n');
}

/**
 * A környezet rövid neve a szerver címéből — a letöltött indító nevébe kerül,
 * hogy ránézésre látszódjon, melyik szerverhez tartozik (stage vagy éles).
 */
export function environmentLabel(apiUrl: string = API_URL): string {
  let host = '';
  try {
    host = new URL(apiUrl).hostname;
  } catch {
    return 'ismeretlen';
  }
  if (host.endsWith('.localhost') || host === 'localhost') return 'helyi';
  if (/(^|\.)stage\./.test(host)) return 'stage';
  if (/(^|\.)dev\./.test(host)) return 'dev';
  return 'eles';
}

/** A fájl letöltése a böngészőből, szerverkérés nélkül — a token nem megy sehova. */
function downloadText(fileName: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Egy IPv4-cím vagy gépnév, opcionális porttal. */
export function isPrinterHost(value: string): boolean {
  return /^[A-Za-z0-9.-]+(:\d{1,5})?$/.test(value.trim());
}

/**
 * Az agent legutóbb jelentett nyomtatócíme, port nélkül (`192.168.88.50:9100` →
 * `192.168.88.50`) — ezzel töltjük ki előre a mezőt.
 */
export function lastKnownPrinterHost(printerHost: string | undefined | null): string {
  if (!printerHost) return '';
  const host = printerHost.replace(/:\d{1,5}$/, '');
  return isPrinterHost(host) ? host : '';
}

/** A letöltött indító neve a nyomtató nevéből és a környezetből. */
export function launcherFileName(agentName: string, extension: 'bat' | 'sh'): string {
  const slug = agentName
    .replace(/[^\w-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `eventbot-nyomtato-${slug || 'agent'}-${environmentLabel()}.${extension}`;
}

interface PrintAgentGuideProps {
  agentName: string;
  /** A nyomtató tárolt címe; ha nincs, az agent legutóbb jelentett címe. */
  printerHost?: string | null;
  /**
   * Épp most kiállított token (a nyomtató létrehozásakor). Ha van, az első
   * letöltés ezt használja, és nem kell újat kérni.
   */
  freshToken?: string | null;
  /** Új token kiállítása a letöltéshez; a korábbi érvényét veszti. */
  onIssueToken: () => Promise<string>;
  /** A cím elmentése a nyomtatóhoz, ha még nincs megadva. */
  onSaveHost: (host: string) => Promise<void>;
}

function CopyableCode({ text }: { text: string }): React.ReactElement {
  return (
    <Paragraph copyable={{ text }} style={{ marginBottom: 8 }}>
      <Text code style={{ wordBreak: 'break-all' }}>
        {text}
      </Text>
    </Paragraph>
  );
}

export function PrintAgentGuide({
  agentName,
  printerHost,
  freshToken,
  onIssueToken,
  onSaveHost,
}: PrintAgentGuideProps): React.ReactElement {
  const { t } = useTranslation();
  const [os, setOs] = useState<DeskOs>(() => detectDeskOs());
  // A letöltéshez kiállított token EBBEN az ablakban: a .bat és a .sh (vagy
  // egy második letöltés) ugyanazt kapja, ne érvénytelenítse egymást.
  const [sessionToken, setSessionToken] = useState<string | null>(freshToken ?? null);
  const [downloading, setDownloading] = useState(false);
  const [hostDraft, setHostDraft] = useState('');
  const host = lastKnownPrinterHost(printerHost);
  const extension = os === 'windows' ? 'bat' : 'sh';
  const fileName = launcherFileName(agentName, extension);

  const download = async () => {
    if (!host) return;
    setDownloading(true);
    try {
      const token = sessionToken ?? (await onIssueToken());
      setSessionToken(token);
      const content =
        os === 'windows'
          ? windowsLauncher(agentName, token, host)
          : linuxLauncher(agentName, token, host);
      downloadText(fileName, content);
    } catch {
      // A közös interceptor már jelezte a hibát.
    } finally {
      setDownloading(false);
    }
  };

  const downloadButton = (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      loading={downloading}
      disabled={!host}
      onClick={sessionToken ? download : undefined}
    >
      {t(
        os === 'windows' ? 'printAgents.guide.downloadWindows' : 'printAgents.guide.downloadLinux'
      )}
    </Button>
  );

  const pythonStep =
    os === 'windows' ? (
      <>
        <Paragraph style={{ marginBottom: 6 }}>{t('printAgents.guide.stepPythonBody')}</Paragraph>
        <Button
          size="small"
          icon={<LinkOutlined />}
          href={PYTHON_WINDOWS_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginBottom: 6 }}
        >
          {PYTHON_WINDOWS_DOWNLOAD_URL}
        </Button>
        <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.stepPythonWinget')}</Paragraph>
        <CopyableCode text={PYTHON_WINGET_COMMAND} />
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('printAgents.guide.stepPythonCheck')}
        </Paragraph>
      </>
    ) : (
      <>
        <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.stepPythonLinux')}</Paragraph>
        <CopyableCode text="python3 --version" />
        <Paragraph style={{ marginBottom: 4 }}>
          {t('printAgents.guide.stepPythonLinuxInstall')}
        </Paragraph>
        <CopyableCode text="sudo apt install python3" />
      </>
    );

  const launcherStep = host ? (
    <>
      <Paragraph style={{ marginBottom: 8 }}>
        {t('printAgents.guide.printerAddress')} <Text code>{host}</Text>
      </Paragraph>
      {sessionToken ? (
        downloadButton
      ) : (
        <Popconfirm
          title={t('printAgents.guide.downloadConfirmTitle')}
          description={
            <div style={{ maxWidth: 360 }}>{t('printAgents.guide.downloadConfirmBody')}</div>
          }
          onConfirm={download}
          okText={t('printAgents.guide.downloadConfirmOk')}
          cancelText={t('common.cancel')}
        >
          {downloadButton}
        </Popconfirm>
      )}
      <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
        {t('printAgents.guide.launcherHint', { file: fileName })}
      </Paragraph>
    </>
  ) : (
    <>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 8 }}
        message={t('printAgents.guide.hostMissing')}
      />
      <Space.Compact>
        <Input
          value={hostDraft}
          onChange={(event) => setHostDraft(event.target.value)}
          placeholder="192.168.88.50"
          status={hostDraft && !isPrinterHost(hostDraft) ? 'error' : undefined}
          aria-label={t('printAgents.guide.hostLabel')}
          style={{ width: 220 }}
        />
        <Button disabled={!isPrinterHost(hostDraft)} onClick={() => onSaveHost(hostDraft.trim())}>
          {t('common.save')}
        </Button>
      </Space.Compact>
    </>
  );

  const startStep =
    os === 'windows' ? (
      t('printAgents.guide.stepStartBody')
    ) : (
      <>
        <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.stepStartLinux')}</Paragraph>
        <CopyableCode text={`bash ${fileName}`} />
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('printAgents.guide.stepStartLinuxHint')}
        </Paragraph>
      </>
    );

  const tokenText = sessionToken || t('printAgents.guide.tokenPlaceholder');
  const hostText = host || t('printAgents.guide.hostPlaceholder');
  const advanced = (
    <>
      <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.advancedDownload')}</Paragraph>
      <Space wrap style={{ marginBottom: 8 }}>
        {AGENT_FILES.map((file) => (
          <Button key={file} size="small" href={agentDownloadUrl(file)}>
            {file}
          </Button>
        ))}
      </Space>
      <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.stepRunNetwork')}</Paragraph>
      <CopyableCode text={agentTcpCommand(tokenText, hostText)} />
      <Paragraph style={{ marginBottom: 4 }}>{t('printAgents.guide.stepRunUsb')}</Paragraph>
      <CopyableCode text={agentUsbCommand(tokenText)} />
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t('printAgents.guide.usbNote')}
      </Paragraph>
    </>
  );

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Text strong>{t('printAgents.guide.title', { name: agentName })}</Text>
        <Segmented<DeskOs>
          value={os}
          onChange={setOs}
          options={[
            { label: 'Windows', value: 'windows' },
            { label: 'Linux', value: 'linux' },
          ]}
        />
      </Space>
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: t('printAgents.guide.stepNetwork'),
            description: t('printAgents.guide.stepNetworkBody'),
          },
          {
            title: t(
              os === 'windows'
                ? 'printAgents.guide.stepPython'
                : 'printAgents.guide.stepPythonLinuxTitle'
            ),
            description: pythonStep,
          },
          { title: t('printAgents.guide.stepLauncher'), description: launcherStep },
          { title: t('printAgents.guide.stepStart'), description: startStep },
          {
            title: t('printAgents.guide.stepCheck'),
            description: t('printAgents.guide.stepCheckBody'),
          },
        ]}
      />
      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        items={[{ key: 'advanced', label: t('printAgents.guide.advanced'), children: advanced }]}
      />
    </div>
  );
}
